// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type Profile = components["schemas"]["ProfileResponse"];

/** Shared query key — the whole My Profile page (info, passkeys, two-factor) reads from this one query. */
export const PROFILE_QUERY_KEY = ["profile"] as const;

/** Back-office user settings are namespaced under `backoffice.` in the shared user_settings table. */
export const SLACK_USERNAME_SETTING = "backoffice.slack.username";

/**
 * The current (signed-in) user's own profile: identity, groups, permissions, passkeys and two-factor
 * state. Pass `select` to read a slice (e.g. just the passkeys) while sharing the single cached request.
 */
export function useProfileQuery<TData = Profile>(select?: (profile: Profile) => TData) {
  return useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: async () => {
      const res = await api.GET("/v1/profile");
      if (res.error || !res.data) throw new Error("Failed to load profile");
      return res.data;
    },
    select,
  });
}

/**
 * Generate a fresh TOTP secret + QR code for setup. Each call mints a NEW secret, so this is a
 * mutation (fired on user action), not a cached query.
 */
export function useGenerateTwoFactorMutation() {
  return useMutation({
    mutationFn: async () => {
      const res = await api.GET("/v1/profile/two-factor");
      if (res.error) throw new Error("Failed to generate two-factor secret");
      return res.data;
    },
  });
}

/** Confirm and enable two-factor by verifying a code generated from the freshly-issued secret. */
export function useEnableTwoFactorMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { secret: string; token: string }) => {
      const res = await api.POST("/v1/profile/two-factor", { body });
      if (res.error) throw new Error("Failed to enable two-factor");
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY }),
  });
}

/** Upsert one or more back-office user settings (keys must be `backoffice.`-prefixed). */
export function useUpdateProfileSettingsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: Record<string, string>) => {
      const res = await api.PATCH("/v1/profile/settings", { body: { settings } });
      if (res.error) throw new Error("Failed to save settings");
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY }),
  });
}

/** Disable two-factor for the current user. */
export function useDisableTwoFactorMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.DELETE("/v1/profile/two-factor");
      if (res.error) throw new Error("Failed to disable two-factor");
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY }),
  });
}
